"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useCountInBeats } from "@/hooks/journey/use-count-in-beats";
import {
  useHandsOnControls,
  useHandsOnPiano,
} from "@/hooks/journey/use-hands-on-controls";
import { useRhythmRun } from "@/hooks/journey/use-rhythm-run";
import { useRollScrub } from "@/hooks/journey/use-roll-scrub";
import { useRunKeyboardShortcuts } from "@/hooks/journey/use-run-keyboard-shortcuts";
import { useTrace } from "@/hooks/journey/use-trace";
import { useStudio } from "@/contexts/studio-context";
import type { LearningJourney } from "@/lib/learning-journey/journey";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
  TOTAL_STEPS,
} from "@/lib/learning-journey/step-mapping";
import type {
  HandsAsked,
  Phrase,
  PhraseFSMSnapshot,
  RunRange,
} from "@/lib/learning-journey/types";
import type { SongControler } from "@/lib/track/controler/song-controler";
import { sustainByGroup } from "../practice";
import type { PracticeHand } from "../practice";
import {
  afterLanding,
  afterMiss,
  arrivalTitle,
  barOfTick,
  bpmAt,
  handsAsked,
  holesLine,
  interruptionLine,
  NEW_RUNG,
  landedPass,
  pulseLine,
  rhythmGroups,
  rhythmicValues,
  stallLine,
  stepRate,
  strayLine,
  RATE_LADDER,
  type RunReading,
  type RungStory,
  wentThrough,
} from "../rhythm";
import {
  afterPassInTime,
  earnsNextRung,
  openingRateInTime,
  resumeBarTick,
  streakLine,
} from "../mastery";
import { FloatingHeader } from "../shell/floating-header";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import {
  JOURNEY_HINT_COLOR_LEFT_PRACTICE,
  JOURNEY_HINT_COLOR_RIGHT_PRACTICE,
  JOURNEY_HINT_COLOR_STRAY,
  litOnsetIndex,
} from "@/hooks/journey/journey-hint-mapping";
import type { Hint } from "@/contexts/hint-context";
import { FloatingShell } from "../shell/floating-shell";
import { HandPill, HandPills, type HandPillState } from "../shell/hand-pills";
import { MiniSteps } from "../shell/mini-steps";
import {
  ACT_FILL,
  NEUTRAL_ACCENT,
  PANEL_PADDING_X_PX,
  PANEL_WIDTH_PX,
  OFFER_CLASS,
  OFFER_INLINE_STYLE,
  OFFER_STYLE,
} from "../shell/panel-metrics";
import { HandsOnHint } from "../shell/hands-on-hint";
import { ACT_H_PX, CompletedFeedback, Progress } from "../shell/run-controls";
import { RunMap, type RunSpan } from "../shell/run-map";
import { TraceBand } from "../shell/trace-band";

/**
 * View for `S4`: the unit **in time**.
 *
 * ## It is no longer an exam
 *
 * This screen used to grade. Two clean passes in a row, at most two errors
 * each, zero missed note, three failures demoting the player to one hand
 * under a banner reading « Tu as échoué 3 fois mains ensemble. » Replayed
 * over 10 528 real work units, that gate killed **100%** of passes on a single
 * forgotten note, failed **15.0%** of units at ordinary human jitter — 98.5%
 * of those with no timing error at all — and passed **62.2%** of units played
 * with no rhythm whatsoever. See `rhythm.ts`.
 *
 * What replaces it is the grammar of S1b and S3: go through it, see what
 * happened, play it again, and move on when *you* decide. The only adverse
 * event is stopping.
 *
 * ## The tempo is not a control any more
 *
 * The step used to open at a tempo computed from the unit's density and offer
 * a link — « ▶ Jouer plus vite » — the player had to notice and press to climb
 * a ladder the screen never said they were on. Both ends of that are gone:
 *
 * - it **opens at the tempo the two-hand practice measured**, which is the
 *   speed the player has just been playing the passage at. Not a corpus
 *   hypothesis, not a record from another day: them, a minute ago;
 * - it **climbs on its own**, one rung per **three** passes that cross the
 *   passage under the right keys in a row (`TEMPO_UP_STREAK`). A passage that
 *   comes out once has been found; a passage that comes out three times
 *   running has been installed, and that is what earns a faster one. The card
 *   says how far the streak has got and where the tempo went; there is
 *   nothing to press.
 *
 * What is still offered is the way *down*, and only after a pass that stopped:
 * slowing is a move made because the passage did not come out, and that one
 * the player has to be able to make.
 *
 * ## Scrolling the roll says where to start
 *
 * A unit is four bars in the median and the fourth is the one that will not
 * come out. The pass takes up at the bar the player scrolled to — a bar and
 * not an attack, because a run in time comes in on a downbeat. It counts no
 * repetition, sets no tempo and climbs nothing: it is a piece of the passage,
 * and the step is about the passage.
 *
 * ## Two heights, and the geometry forces it
 *
 * At 560 px — the width every previous screen uses — a full panel on a
 * 419 px roll ends well below the playhead, which sits 319 px down: **it
 * covers the playhead**, and the roll is what the player follows during a
 * pass.
 *
 * So the panel is not one shape capped by a flag. It is **two**: a full one
 * before the pass, where nothing is moving and the player is reading, and a
 * band of about 80 px during it, where the roll is the screen and the panel
 * has one thing to say. The stepper goes with it — nobody navigates in the
 * middle of a run.
 *
 * ## The pulse is not in here
 *
 * The visible beat lives on the roll: `piano-roll/measures.ts` already draws
 * bar lines, beat lines and the beat number, gated on a flag this step now
 * turns on (see `useRhythmRun`). A 3 px ribbon 700 px from where the eyes are
 * is not seen in peripheral vision; a line across the roll is.
 *
 * ## Everything tinted here would claim a hand
 *
 * Same rule as S3: `--color-accent` is twelve degrees of hue from
 * `--color-hand-right`, and the teal that used to paint « run validé » is one
 * degree from `--color-hand-left` — the success of this step was drawn in the
 * colour of the left hand. The chrome is achromatic; only the two hands and
 * the white of the primary action carry colour.
 */

const LEAD_BOX_H_PX = 34;

/** The order at the top of the panel. Same scale as the steps before. */
const HERO_FONT_PX = 38;

type RhythmStateProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly phraseLabel: string;
  readonly journey: LearningJourney;
  readonly rangeRunner: RangeRunner | null;
  readonly songControler: SongControler | null;
  readonly bpm: number;
  readonly ppq: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
};

/**
 * `S4` itself: the screen above, bound to the phrase FSM.
 *
 * The binding is all that is specific to phase 1 — the tempo the practice
 * step handed over, where "Continuer" leads, and a stepper counting the steps
 * of a unit. Phase 2 needs the same screen with none of those, which is why
 * they live here and not in `UnitInTime`.
 */
export function RhythmState({
  phrase,
  phraseSnapshot,
  journey,
  rangeRunner,
  songControler,
  bpm,
  ppq,
  ticksPerBeat,
  ticksPerBar,
}: RhythmStateProps) {
  const stepIndex = phraseStateToStepIndex(phraseSnapshot.state);
  // What this unit's stepper counts: the steps it actually has. A
  // right-hand-only unit has three, not five with two dead slots.
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );

  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handleContinue = useCallback(() => {
    journey.completeRhythmRun("validated");
  }, [journey]);

  const handleBack = useCallback(() => {
    journey.stepBack();
  }, [journey]);

  /**
   * The player puts the metronome down.
   *
   * `leaveTime` and not `stepBack`, though on a two-hand unit the two land on
   * the same state: this is a switch, not a walk backwards, and the two are
   * told apart by what they cost. A jump from the stepper drops the tempo the
   * practice measured (`fastForwardTo`); the switch keeps it, so coming back
   * in time reopens where the player left rather than at what the unit is
   * remembered by.
   */
  const handleLeaveTime = useCallback(() => {
    journey.leaveTime();
  }, [journey]);

  return (
    <UnitInTime
      phrase={phrase}
      unitIndex={journey.getSnapshot().currentPhraseIndex}
      journey={journey}
      rangeRunner={rangeRunner}
      songControler={songControler}
      bpm={bpm}
      ppq={ppq}
      ticksPerBeat={ticksPerBeat}
      ticksPerBar={ticksPerBar}
      pace={phraseSnapshot.pace}
      onContinue={handleContinue}
      kicker="EN RYTHME"
      stepIndex={stepPosition(steps, stepIndex)}
      stepTotal={steps.length}
      footer={
        <MiniSteps
          active={stepIndex}
          steps={steps}
          reachable={reachable}
          onJump={handleJump}
          // Back out of the metronome is « Retour »: it lands on the free
          // régime and keeps the tempo, where the switch used to be.
          onBack={
            phraseSnapshot.canLeaveTime
              ? handleLeaveTime
              : phraseSnapshot.canStepBack
                ? handleBack
                : undefined
          }
        />
      }
    />
  );
}

export type UnitInTimeProps = {
  readonly phrase: Phrase;
  /** Index of the unit in the journey's plan, for its remembered tempo. */
  readonly unitIndex: number;
  readonly journey: LearningJourney;
  readonly rangeRunner: RangeRunner | null;
  readonly songControler: SongControler | null;
  readonly bpm: number;
  readonly ppq: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
  /**
   * Tempo the two-hand practice measured, as a fraction of the written one.
   *
   * The step opens on it when it is there. `null` or absent in phase 2, where
   * a unit is taken up again without a practice run in front of it, and the
   * screen falls back to what the unit is remembered by. See
   * `openingRateInTime`.
   */
  readonly pace?: number | null;
  /** What « Continuer → » does. */
  readonly onContinue: () => void;
  /**
   * First half of the kicker: `À REPRENDRE` in phase 2, `EN RYTHME` on a unit
   * that draws no switch. `null` when the switch says it instead.
   */
  readonly kicker: string | null;
  /** Shown at the foot, at rest. A stepper in S4, a way back in phase 2. */
  readonly footer: React.ReactNode;
  readonly stepIndex?: number;
  /** How many steps this unit has; see `stepPosition`. */
  readonly stepTotal?: number;
  /**
   * Caps the tempo the unit opens at.
   *
   * Phase 2 passes the piece's tempo: a unit that already holds faster than
   * the piece is being taken up again *because the piece does not hold there*,
   * and reworking it above the tempo of the thing it is part of would train
   * the wrong speed.
   */
  readonly maxRate?: number;
};

export function UnitInTime({
  phrase,
  unitIndex,
  journey,
  rangeRunner,
  songControler,
  bpm,
  ppq,
  ticksPerBeat,
  ticksPerBar,
  pace = null,
  onContinue,
  kicker,
  footer,
  stepIndex,
  stepTotal = TOTAL_STEPS,
  maxRate,
}: UnitInTimeProps) {
  const titleId = useId();
  const { studio } = useStudio();

  // What the screen says it is asking for. See `handsAsked`.
  const asked = useMemo(() => handsAsked(phrase), [phrase]);
  const allGroups = useMemo(() => rhythmGroups(phrase), [phrase]);

  // ── Where the pass starts ────────────────────────────────────

  /**
   * Bar the player scrolled the roll to, or `null` for the whole unit.
   *
   * Reset on a change of unit during render rather than in an effect, like
   * the tempo and the counters below: an effect would paint one frame of the
   * previous unit's resume point.
   */
  const [resumeTick, setResumeTick] = useState<number | null>(null);
  const [resumeUnit, setResumeUnit] = useState(phrase.id);

  if (resumeUnit !== phrase.id) {
    setResumeUnit(phrase.id);
    setResumeTick(null);
  }

  const groups = useMemo(
    () =>
      resumeTick === null
        ? allGroups
        : allGroups.filter((group) => group.startTick >= resumeTick),
    [allGroups, resumeTick],
  );

  /**
   * The span the pass plays, which is the unit unless the player asked to
   * take it up part-way through.
   *
   * Built field by field rather than spread over the phrase: `startMeasure`
   * is read downstream and a spread would leave it naming the unit's first
   * bar for a span that starts at its third. That is exactly the class of lie
   * `RunRange` was extracted to prevent.
   */
  const range = useMemo<RunRange>(() => {
    if (resumeTick === null) {
      return phrase;
    }

    const barsIn =
      ticksPerBar > 0 ? (resumeTick - phrase.startTick) / ticksPerBar : 0;

    return {
      id: phrase.id,
      startTick: resumeTick,
      endTick: phrase.endTick,
      startMeasure: phrase.startMeasure + barsIn,
      endMeasure: phrase.endMeasure,
      notesByHand: phrase.notesByHand,
    };
  }, [phrase, resumeTick, ticksPerBar]);

  /** The bar of the unit the pass takes up at, 1-based. `1` for the whole. */
  const resumeBar =
    resumeTick === null
      ? 1
      : (barOfTick(resumeTick, phrase.startTick, ticksPerBar) ?? 1);

  // A hand that holds is a hand that is playing: the rail lights it, and the
  // score is what says so. See `sustainByGroup`.
  const sustaining = useMemo(
    () => sustainByGroup(phrase, groups),
    [phrase, groups],
  );

  // ── Tempo ────────────────────────────────────────────────────

  const index = unitIndex;
  // From the **whole unit**, never from the stretch the player asked to take
  // up: a resumed pass covers three bars of four, and opening it at the tempo
  // three bars are worth would make the passage a different one every time
  // the player scrolls. A unit has one tempo.
  //
  // What it opens at is, in order, what the practice run measured, what the
  // unit has held before, and what its density says. See `openingRateInTime`.
  const openingRate = useMemo(
    () =>
      openingRateInTime(
        pace,
        journey.unitRate(index),
        allGroups,
        bpm,
        ppq,
        maxRate,
      ),
    [pace, journey, index, allGroups, bpm, ppq, maxRate],
  );
  const [rate, setRate] = useState(openingRate);
  const [rateUnit, setRateUnit] = useState(phrase.id);

  // The tempo belongs to the unit, so it survives a resumed pass over part of
  // it. Adjusted during render rather than in an effect: this is state
  // derived from a change of subject, and an effect would show one frame of
  // the previous unit's tempo before correcting itself.
  if (rateUnit !== phrase.id) {
    setRateUnit(phrase.id);
    setRate(openingRate);
  }

  const effectiveBpm = bpmAt(bpm, rate);

  // ── The pass ─────────────────────────────────────────────────

  const [accompanyHand, setAccompanyHand] = useState<PracticeHand | null>(null);
  const [spentAccompaniment, setSpent] = useState<ReadonlySet<PracticeHand>>(
    () => new Set(),
  );
  const [passes, setPasses] = useState(0);
  const [stops, setStops] = useState(0);
  const [story, setStory] = useState<RungStory>(NEW_RUNG);
  const [tapping, setTapping] = useState(false);
  /**
   * The rung the tempo went up to on the pass that just settled, or `null`.
   *
   * Kept beside the rate rather than derived from it: the card has to name
   * where the tempo *went*, and by the time it renders `rate` is already
   * there. Cleared when the next pass starts.
   */
  const [climbedTo, setClimbedTo] = useState<number | null>(null);
  /**
   * Passes that have held in a row at this tempo. Back to zero on anything
   * that breaks the run of them, and on the rung it earns.
   */
  const [streak, setStreak] = useState(0);
  /**
   * How many keys the pass that just settled actually struck.
   *
   * Read off the live lane at the instant the pass settles, never derived
   * from the reading: `honoured` counts *answered onsets*, so a player who
   * spent the passage on the wrong keys has zero honoured and forty strikes —
   * and that is precisely the pass the trace exists for. Read later it would
   * be a promise about a lane the next pass has already emptied.
   */
  const [strikes, setStrikes] = useState(0);
  const [countedUnit, setCountedUnit] = useState(phrase.id);

  if (countedUnit !== phrase.id) {
    setCountedUnit(phrase.id);
    setPasses(0);
    setStops(0);
    setStory(NEW_RUNG);
    setTapping(false);
    setClimbedTo(null);
    setStreak(0);
    setStrikes(0);
    setAccompanyHand(null);
    setSpent(new Set());
  }

  /** The pass covers part of the unit, because the player scrolled to a bar. */
  const partial = resumeTick !== null;

  /**
   * A pass stopped, or came out under the wrong keys: the run of passes that
   * hold starts again.
   *
   * A pass over **part** of the unit breaks nothing, for the same reason it
   * counts for nothing: three bars of four are not the passage the streak is
   * a claim about.
   */
  const breakStreak = useCallback(() => {
    if (partial) {
      return;
    }

    setStreak(0);
  }, [partial]);

  /**
   * A pass that reached the end is what the unit is remembered by: the
   * chaining step opens at the slower of the two units it joins, and without
   * this it opened at a constant nobody had ever held.
   */
  const handleFinished = useCallback(
    (finished: { reading: RunReading; rate: number }) => {
      // One pass per offer: a scaffold left in place becomes the way the
      // struggling player always plays, which is the opposite of the point.
      setAccompanyHand(null);
      setStrikes(studio?.liveStrikeCount() ?? 0);

      if (finished.reading.interruptions.length > 0) {
        setStops((n) => n + 1);

        if (!tapping) {
          setStory(afterMiss);
          breakStreak();
        }

        return;
      }

      if (tapping) {
        // Beating the rhythm out is a rehearsal for the pass, not one of
        // them: it counts no repetition and sets no tempo the unit is
        // remembered by, because no note was played. Nor does it enter the
        // story of the rung: nothing was played beside anything.
        return;
      }

      // Landing is crossing the passage **with the notes**: see `landedPass`.
      // A pass that skated over a third of them is not a repetition of
      // anything, and counting it filled the card's dots, turned the primary
      // button into « Continuer » at the fourth, and set the tempo the unit
      // is remembered by.
      if (!landedPass(finished.reading)) {
        setStory(afterMiss);
        breakStreak();

        return;
      }

      setStory(afterLanding);

      // A stretch of the unit is not the unit. It counts no repetition, sets
      // no tempo the unit is remembered by, and moves no rung: the player
      // took up three bars of four, and the claim « this passage holds at 78 »
      // is about the passage.
      if (partial) {
        return;
      }

      setPasses((n) => n + 1);
      journey.recordUnitRate(finished.rate);

      // The tempo climbs on its own, and this is the whole of it: three
      // passes that cross the passage under the right keys, in a row, earn
      // the next rung. No link to notice, nothing to press. One clean pass
      // says the passage has been *found*; three say it has been installed,
      // which is the claim a faster tempo rests on.
      const held = afterPassInTime(streak, finished.reading);

      if (!earnsNextRung(held)) {
        setStreak(held);

        return;
      }

      const next = stepRate(finished.rate, 1);

      // At the top of the ladder the streak stays full rather than resetting:
      // there is no rung to spend it on, and zeroing it would promise one.
      if (next <= finished.rate) {
        setStreak(held);

        return;
      }

      setStreak(0);
      setRate(next);
      setClimbedTo(next);
    },
    [journey, studio, tapping, partial, streak, breakStreak],
  );

  const appHand = accompanyHand === "L" ? "left" : accompanyHand === "R" ? "right" : null;

  const run = useRhythmRun({
    journey,
    rangeRunner,
    range,
    groups,
    bpm,
    ppq,
    ticksPerBar,
    rate,
    state: "S4",
    ignorePitch: tapping,
    auditionSong: false,
    accompanyHand: appHand,
    songControler,
    onFinished: handleFinished,
  });

  const reading = run.lastRun?.reading ?? null;
  const through = reading !== null && wentThrough(reading);
  // No reading is not "dirty": before a pass there is nothing to hold back.
  // The same bar as the repetition counter and the story: one screen cannot
  // hold two definitions of a pass that held.
  const clean = reading === null || landedPass(reading);
  // What the pass amounted to, computed once so the panel and the screen
  // reader say the same thing — and so neither indexes into a stop list that
  // a unit of a single onset can never fill.
  const verdict: string | null =
    reading === null
      ? null
      : reading.interruptions.length > 0
        ? interruptionLine(
            reading.interruptions[0],
            phrase.startTick,
            ticksPerBar,
          )
        : through
          ? null
          : // No stop and nothing answered. `STOP_MISSED_ONSETS` asks for two
            // missed onsets in a row, which a hand carrying one onset can
            // never supply, so this is the only true thing left to say.
            "Ce passage n’a pas été joué.";
  /**
   * What the pass fell short by, or nothing.
   *
   * The keys beside first: below the tolerance they change nothing, and above
   * it they are the thing standing between the player and the rung. Then the
   * attacks nobody answered, which is the softer of the two things to be
   * told and, until `holesLine` existed, the one thing a silent pass full of
   * holes was never told at all.
   */
  const shortfall: string | null =
    reading === null ? null : (strayLine(reading) ?? holesLine(reading));
  /**
   * The note under the arrival card's title, computed once so the panel and
   * the screen reader cannot drift apart.
   *
   * Where the tempo went comes first, because it is the one thing on this
   * card the player did not already know: the pass they just made earned the
   * next rung and the passage will be faster when they press again. Saying it
   * is what makes the climb theirs rather than something the screen did
   * behind their back.
   *
   * Then what the pass fell short by, when it fell short of anything: the
   * pulse is a remark, this is the thing with an act attached.
   *
   * Except on the pass that lands after several that did not. There the
   * shortfall is held back for one card: the player has just done the thing,
   * and answering with what was still wrong is how an app makes someone put
   * it down. It comes back on the next pass, which is one button away.
   */
  const arrivalNote: string | null =
    climbedTo !== null
      ? `Le tempo monte à ${bpmAt(bpm, climbedTo)} BPM.`
      : (streakLine(streak) ??
        (story.salute
          ? pulseLine(reading?.pulse ?? null)
          : (shortfall ?? pulseLine(reading?.pulse ?? null))));
  const countIn = useCountInBeats();

  // ── The trace ────────────────────────────────────────────────

  /**
   * Where the reading of the pass begins.
   *
   * On the stop when there was one, one bar early: the playhead sits about a
   * sixth of the way down the roll, so parking exactly on it would put the
   * lead-in under the past gradient, and the lead-in is half of what there is
   * to understand.
   *
   * Otherwise on the first attack nobody answered, which is the other thing
   * a pass leaves to look at and the one this step now names in words. And
   * when there is neither, at the top: a trace of a pass that held is not a
   * diagnosis, it is the passage in the player's own hand.
   */
  const traceParkTick = useMemo(() => {
    if (reading === null) {
      return null;
    }

    const stop = reading.interruptions[0];

    if (stop !== undefined) {
      return Math.max(range.startTick, stop.startTick - ticksPerBar);
    }

    const hole = reading.honoured.indexOf(false);

    if (hole >= 0 && groups[hole] !== undefined) {
      return Math.max(range.startTick, groups[hole].startTick - ticksPerBar);
    }

    return range.startTick;
  }, [reading, range.startTick, ticksPerBar, groups]);

  const trace = useTrace(traceParkTick);
  const traceHide = trace.hide;
  /**
   * Offered only when the lane holds something.
   *
   * A pass where the player touched nothing leaves it empty, and an offer
   * that opens onto a blank roll is worse than no offer. `strikes` and not
   * `reading`: the pass that is hardest to make sense of is the one where
   * everything was struck and nothing was answered.
   */
  const canTrace = reading !== null && strikes > 0 && !tapping;

  const handleStart = useCallback(() => {
    // Before the pass, not after: the capture and the live lane are both
    // emptied when a run is armed, so a trace left open would spend the
    // count-in drawing contours that are about to be deleted.
    traceHide();
    setClimbedTo(null);
    run.clear();
    run.start();
  }, [run, traceHide]);

  const handleTap = useCallback(() => {
    setTapping(true);
    run.clear();
  }, [run]);

  const handleAddNotes = useCallback(() => {
    setTapping(false);
    run.clear();
  }, [run]);

  const handleAccompany = useCallback(
    (hand: PracticeHand) => {
      setAccompanyHand(hand);
      setSpent((prev) => new Set([...prev, hand]));
      run.clear();
    },
    [run],
  );

  /**
   * Changing the tempo plays the passage at it, and does not merely arm it.
   *
   * As a setting it looked broken: the offer sat under a « Jouer » button, so
   * pressing it moved two small numbers on the panel and left the player in
   * front of the same screen, with the act they had just asked for still
   * undone. The offer is not "set a slower tempo", it is "play this slower",
   * and it takes one press.
   *
   * Through a ref rather than by calling `start()` here: the run reads the
   * rate off its props, so a start in this tick would go at the old tempo.
   * The flag is spent on the very next render, which is the one carrying the
   * new rate.
   */
  const playAfterRate = useRef(false);

  const handleStepTempo = useCallback((direction: -1 | 1) => {
    playAfterRate.current = true;
    // The streak is a claim about a tempo, so it does not survive one. Three
    // passes held at 72 say nothing about 60.
    setStreak(0);
    setRate((current) => stepRate(current, direction));
  }, []);

  useEffect(() => {
    if (!playAfterRate.current) {
      return;
    }

    playAfterRate.current = false;
    handleStart();
  }, [rate, handleStart]);

  useRunKeyboardShortcuts({
    status: run.status,
    hasFeedback: run.lastRun !== null,
    onStart: handleStart,
    onRetry: handleStart,
    onCancel: run.cancel,
  });

  /**
   * The player scrolled the roll: the pass takes up at that bar.
   *
   * Off while a pass runs — a gesture on the roll pauses the transport, so
   * the run owns it for its duration and the band's one way out is « Je
   * m'arrête ». Scrolling back to the first bar is how the whole unit comes
   * back, which is the same gesture rather than a second control.
   */
  const handleScrub = useCallback(
    (tick: number) => {
      const resume = resumeBarTick(
        tick,
        phrase.startTick,
        phrase.endTick,
        ticksPerBar,
      );

      setResumeTick(resume);

      // Snapped to the bar the pass will actually start on, so the playhead
      // is standing where the count-in will hand over. Only when there is
      // one: a scroll that lands outside the unit is the player going
      // somewhere else, and pulling them back would fight the chip that
      // offers to work the passage they scrolled to.
      if (resume !== null) {
        void studio?.animateScrollToTick(resume);
      }
    },
    [studio, phrase.startTick, phrase.endTick, ticksPerBar],
  );

  // Off while the trace is open: following a contour means scrolling the
  // roll, and that gesture is how this step arms a pass over part of the
  // unit. Reading what you played would silently re-aim what you play next.
  useRollScrub(run.status === "idle" && !trace.open, handleScrub);

  /** Put the whole unit back, and the roll with it. */
  const handleWholeUnit = useCallback(() => {
    setResumeTick(null);
    void studio?.animateScrollToTick(phrase.startTick);
  }, [studio, phrase.startTick]);

  // ── Drawing ──────────────────────────────────────────────────

  // The span the map draws is the span the pass plays, so a resumed pass
  // shows the three bars it covers rather than four with a dead one.
  const span = useMemo<RunSpan>(
    () => ({
      startTick: range.startTick,
      endTick: range.endTick,
      ticksPerBeat,
      ticksPerBar,
    }),
    [range, ticksPerBeat, ticksPerBar],
  );
  const running = run.status === "running";
  const handsOn = useHandsOnPiano();

  // Starting the pass without reaching for anything.
  //
  // The pedal launches. So does playing — but before the first pass, only the
  // unit's **opening group**: the panel is showing the keyboard to be read,
  // and a player trying a key against that map has not asked for a clock to
  // start. Once a pass has happened the map is gone and the panel is offering
  // to go again, so anything struck is a restart.
  //
  // Two passes have no opening group to ask for. The tapped one is played on
  // whatever key falls under the hand, and the accompanied one leaves the
  // other hand to the application — whose notes are filtered out of the
  // gesture, so a group spanning both could never be completed.
  const opening =
    run.lastRun !== null || tapping || accompanyHand !== null
      ? null
      : (groups[0]?.midis ?? null);

  // Nothing behind the trace band is launchable: a player leaning on the
  // keyboard to work out what their hand did must not start the pass that
  // erases the lane they are reading.
  useHandsOnControls({
    enabled: !running && !through && !trace.open,
    onAdvance: handleStart,
    onPlay: handleStart,
    playMidis: opening,
  });

  // The tapped-through card is the one end-of-pass card that is not a
  // `CompletedFeedback`: it has a single act, and the foot can take it.
  useHandsOnControls({
    enabled: !running && through && tapping && !trace.open,
    onAdvance: handleAddNotes,
  });

  // ── The keys, on the computer keyboard ───────────────────────
  //
  // The layout pill was lit through discovery and practice and went dark at
  // the exact step where the passage has to come out under the fingers, in
  // time — i.e. where a player on a computer keyboard needs it most. Nothing
  // deliberate: the step simply never published a hint.
  //
  // What it publishes is the onset **being played**: while a pass runs that is
  // the last group the playhead has gone past, and before the first one — at
  // rest, during the count-in — the opening group, which is where the hands go
  // before pressing start.
  //
  // It used to publish the onset *about to be played*, one ahead. The keyboard
  // then said something else than the roll: at the instant a note had to be
  // struck its key went dark and the next one lit up, while the roll's own
  // layer still lit the key that was sounding. Two halos at once, and the one
  // wearing the note's name was the one not to play yet. See `litOnsetIndex`.
  const litGroup = groups[litOnsetIndex(running ? run.playedIndex : 0, groups.length)];
  const hint = useMemo<Hint>(
    () => ({
      source: HINT_SOURCE,
      // Coloured by hand, like every other step: someone who has learnt
      // "turquoise is my left hand" over three states must not have to
      // relearn it at the fourth.
      midis: [
        ...(litGroup?.notes ?? []).map((note) => ({
          midi: note.midi,
          color:
            note.hand === "L"
              ? JOURNEY_HINT_COLOR_LEFT_PRACTICE
              : JOURNEY_HINT_COLOR_RIGHT_PRACTICE,
        })),
        // The key that was just struck for nothing, lit under the finger that
        // struck it. Named on the instrument and not on a counter, because
        // six bars later the player no longer knows which one it was — and
        // never in a hand colour: a wrong key belongs to no hand.
        //
        // It cannot collide with the lit group: a stray is by definition a
        // pitch the passage asks for nowhere near this instant.
        ...run.strays.flashing.map((midi) => ({
          midi,
          color: JOURNEY_HINT_COLOR_STRAY,
        })),
      ],
    }),
    [litGroup, run.strays.flashing],
  );

  useHintPublisher(hint);

  const bars = Math.max(1, Math.ceil((phrase.endTick - phrase.startTick) / ticksPerBar));
  const currentBar =
    barOfTick(run.playedTick ?? phrase.startTick, phrase.startTick, ticksPerBar) ?? 1;

  /**
   * Once the passage runs, the panel is a band across the roll.
   *
   * The same rule the practice steps follow: while the player is reading, a
   * panel; the moment the roll starts moving, the roll *is* the screen and
   * what is left to say fits on one line. The panel it replaces was 360 px of
   * column holding a row of about 44 — it was already a band, drawn inside a
   * frame that had nothing else in it, and the frame took a third of the roll
   * with it.
   *
   * The header goes with the frame. Its two facts are worth keeping and cost
   * a few characters on the line: which hands are asked for, and at what
   * tempo. The stepper does not come: the way out of a running passage is
   * « Je m'arrête », which is the widest thing on the band for that reason.
   */
  if (running) {
    return (
      <FloatingShell
        variant="band"
        ariaLabel="En rythme"
        titleId={titleId}
        contentKey="rhythm-band"
      >
        <RunningBand
          titleId={titleId}
          kicker={composeKicker(kicker, asked)}
          bpm={effectiveBpm}
          percent={
            groups.length < 2
              ? 0
              : (Math.min(run.playedIndex, groups.length - 1) /
                  (groups.length - 1)) *
                100
          }
          mode={asked}
          playerHand={
            accompanyHand === null ? null : accompanyHand === "L" ? "R" : "L"
          }
          countIn={countIn}
          currentBar={currentBar}
          bars={bars}
          strays={run.strays.count}
          onCancel={run.cancel}
        />
      </FloatingShell>
    );
  }

  return (
    <FloatingShell
      variant="floating"
      width={PANEL_WIDTH_PX}
      ariaLabel="En rythme"
      titleId={titleId}
    >
      <FloatingHeader
        stepIndex={stepIndex}
        stepTotal={stepTotal}
        kicker={kicker ?? "EN RYTHME"}
        titleId={titleId}
        trailing={<TempoReading bpm={effectiveBpm} />}
      />

      {trace.open ? (
        <TraceBand
          stopBar={
            reading?.interruptions[0] === undefined
              ? null
              : barOfTick(
                  reading.interruptions[0].startTick,
                  phrase.startTick,
                  ticksPerBar,
                )
          }
          // No hint: a unit is a few bars and the park has just put the place
          // that matters under the playhead. Telling this player to scroll
          // would send them looking for something already under their eyes.
          hint={null}
          onClose={trace.hide}
        />
      ) : (
      <div style={{ padding: `26px ${PANEL_PADDING_X_PX}px 20px` }}>
        <p
          className="font-mono"
          style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "var(--color-text-secondary)",
          }}
        >
          {modeKicker(asked)}
        </p>

        {/*
          The order, before a pass, as on the steps before. After one the
          panel's subject is what happened, which the line below carries.
        */}
        {run.lastRun === null && run.stalled === null && !tapping && (
          <p
            style={{
              margin: "10px 0 0",
              fontSize: HERO_FONT_PX,
              lineHeight: 1.08,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: "var(--color-text-primary)",
            }}
          >
            À toi de jouer
          </p>
        )}

        <div style={{ minHeight: through ? 0 : LEAD_BOX_H_PX, marginTop: 10 }}>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.45,
              color: "var(--color-text-secondary)",
              textWrap: "pretty",
              margin: 0,
            }}
          >
            {run.lastRun === null
              ? run.stalled !== null
                ? stallLine(run.stalled)
                : tapping
                ? "Frappe juste le rythme, sur n’importe quelle touche."
                : accompanyHand !== null
                  ? `Je joue la main ${accompanyHand === "L" ? "gauche" : "droite"}. Joue l’autre.`
                  : leadLine(
                  asked,
                  bpm,
                  effectiveBpm,
                  passes,
                  resumeBar,
                  pace !== null,
                )
              : verdict}
          </p>
        </div>

        <RunMap
          groups={groups}
          hands={
            accompanyHand === null
              ? mapHands(asked)
              : [accompanyHand === "L" ? "R" : "L"]
          }
          span={span}
          honoured={reading?.honoured}
          sustaining={sustaining}
        />

        {/*
          Under the map, because it is the same question answered twice over.
          The map says *which* attacks went unanswered and can say no more —
          the trace is the only thing on this screen that can say what the
          hand did instead.
        */}
        {canTrace && (
          <div className="flex justify-end" style={{ height: 16 }}>
            <button
              type="button"
              onClick={trace.show}
              className={`${OFFER_CLASS} shrink-0`}
              style={OFFER_INLINE_STYLE}
            >
              Voir ce que tu as joué
            </button>
          </div>
        )}

        {through && tapping ? (
          <TappedThrough
            note={pulseLine(reading?.pulse ?? null)}
            onAddNotes={handleAddNotes}
          />
        ) : through ? (
          <CompletedFeedback
            passes={passes}
            onRetry={handleStart}
            onContinue={onContinue}
            handColor={ACT_FILL}
            // Under the map and the trace offer: see `both-hands-state`.
            divided
            title={arrivalTitle(story, "Tu es allé au bout.")}
            note={arrivalNote}
          />
        ) : (
          <>
            <PrimaryAction
              label={
                run.lastRun === null
                  ? tapping
                    ? "Frapper le rythme"
                    : "Jouer"
                  : "Reprendre"
              }
              onClick={handleStart}
            />

            {handsOn && (
              <HandsOnHint
                items={[
                  {
                    gesture: run.lastRun === null ? "Joue" : "Rejoue",
                    effect:
                      run.lastRun !== null
                        ? "reprendre"
                        : opening === null
                          ? "et le décompte part"
                          : "la première note lance le décompte",
                  },
                  { gesture: "Pédale", effect: "lancer" },
                ]}
              />
            )}
          </>
        )}

        {/*
          Nothing to offer next to the rhythm card: it carries the single
          act that follows, and a row underneath offering to tap again —
          or to change the tempo of an act that just worked — would be
          three ways out of a screen that has one.
        */}
        {!(through && tapping) && (
        <Offers
          rate={rate}
          bpm={bpm}
          wentThrough={through}
          clean={clean}
          stopped={reading !== null && reading.interruptions.length > 0}
          partial={partial}
          canAccompany={
            asked === "both" && songControler?.hasHandLanes() === true
          }
          spentAccompaniment={spentAccompaniment}
          tapping={tapping}
          canTap={stops > 0 && rhythmicValues(groups, ppq) >= 2}
          onStepTempo={handleStepTempo}
          onWholeUnit={handleWholeUnit}
          onAccompany={handleAccompany}
          onTap={handleTap}
          onAddNotes={handleAddNotes}
        />
        )}

        <div aria-live="polite" className="sr-only">
          {run.lastRun === null
            ? ""
            : through
              ? tapping
                ? "C’est ça, le rythme est là."
                : // The card's own two sentences, and not a fixed one. A pass
                  // with holes in it reads « Tu n’as pas lâché » under a rail
                  // of hollow cells; announcing « Tu es allé au bout » handed
                  // the player who cannot see that rail exactly the approval
                  // the card withholds.
                  [arrivalTitle(story, "Tu es allé au bout."), arrivalNote]
                    .filter((part) => part !== null)
                    .join(" ")
              : (verdict ?? "")}
        </div>
      </div>
      )}

      {footer}
    </FloatingShell>
  );
}

/** One slot in the hint layer; re-publishing replaces it in place. */
const HINT_SOURCE = "journey-rhythm";



/** The band's bar carries no landmark: a run in time has no hand moves to mark. */
const NO_MARKERS: ReadonlySet<number> = new Set();

// ── The band shown while the passage runs ─────────────────────────

/**
 * One row across the roll, and the stepper gone.
 *
 * Shared with the chaining step, which is the same act on a longer range:
 * one band and not two, so the two steps cannot drift apart on a screen the
 * player crosses in the same minute.
 *
 * Everything the player needs during the pass is on the roll — the notes
 * coming, the beat grid, the playhead. What the band owes them is where the
 * passage is and how to stop, and nothing else: a second progress object next
 * to a scrolling roll is a second clock.
 *
 * The bar number is the only thing that moves, twice on a median unit. Slow
 * enough to be read out of the corner of an eye, which a beat-rate blink is
 * not — and which WCAG 2.2.2 would require a way to stop, in the middle of a
 * timed exercise.
 */
export function RunningBand({
  titleId,
  kicker,
  bpm,
  percent,
  mode,
  playerHand,
  countIn,
  currentBar,
  bars,
  strays,
  onCancel,
}: {
  titleId: string;
  /** `EN RYTHME · MAIN DROITE`, the header's line, kept as a label. */
  kicker: string;
  /** The tempo actually being played, not the piece's. */
  bpm: number;
  /** How much of the passage the playhead has crossed, 0 to 100. */
  percent: number;
  mode: HandsAsked;
  /**
   * Set during an accompanied pass: the one hand that is the player's. The
   * other is sounding — the application has it — and none of the three pill
   * states can say that without lying, so it is not drawn at all.
   */
  playerHand: PracticeHand | null;
  countIn: { beat: number; total: number } | null;
  currentBar: number;
  bars: number;
  /** Keys struck for nothing so far in this pass. */
  strays: number;
  onCancel: () => void;
}) {
  const stateOf = (hand: PracticeHand): HandPillState => {
    if (mode === "both") {
      return "plays";
    }

    return (mode === "lh-only") === (hand === "L") ? "plays" : "waits";
  };

  return (
    <div
      className="flex items-center"
      style={{ gap: 12, padding: `10px ${PANEL_PADDING_X_PX}px` }}
    >
      {/*
        Both flanks take `flex: 1 1 0`, so the one thing that moves sits on
        the axis of the roll whatever is beside it. See the practice band.
      */}
      <div
        className="flex items-center"
        style={{ gap: 14, flex: "1 1 0", minWidth: 0 }}
      >
        <span
          id={titleId}
          className="font-mono shrink-0"
          style={{
            fontSize: 11,
            letterSpacing: "0.12em",
            color: "var(--color-text-secondary)",
          }}
        >
          {kicker}
        </span>

        <span
          aria-hidden
          style={{
            width: 1,
            height: 14,
            background: "var(--color-border-subtle)",
          }}
        />

        {playerHand === null ? (
          <HandPills stateOf={stateOf} marginBottom={0} />
        ) : (
          <HandPill hand={playerHand} state="plays" />
        )}
      </div>

      {/*
        The middle holds the one thing that moves, and during the count-in
        that is the count-in.

        The bar is the practice band's, and it is the same claim: how far
        into the passage you are. It advances **per onset** — `playedIndex`
        is the group the playhead has reached — so it is not the beat-rate
        blink this band was written against; it steps about thirteen times
        on a median unit, next to a label that steps four.
      */}
      <div
        className="flex items-center justify-center shrink-0"
        style={{ gap: 12 }}
      >
        {countIn !== null ? (
          <CountInDots beat={countIn.beat} total={countIn.total} />
        ) : (
          <>
            <span style={{ width: 120 }}>
              <Progress
                percent={percent}
                markers={NO_MARKERS}
                total={0}
                color={NEUTRAL_ACCENT}
                compact
              />
            </span>

            <span
              className="font-mono tabular-nums"
              style={{ fontSize: 12, color: "var(--color-text-secondary)" }}
            >
              mesure {currentBar} / {bars}
            </span>
          </>
        )}
      </div>

      <div
        className="flex items-center justify-end"
        style={{ gap: 14, flex: "1 1 0", minWidth: 0 }}
      >
        {/*
          The second thing that moves in this band, and the exception is
          deliberate: it moves on an *event* the player caused, not on a clock.
          The rule this band was written around forbids a beat-rate blink next
          to a scrolling roll — a tally that ticks four times in a pass is not
          one, and it is the only place the count can be read while there is
          still something to do about it.
        */}
        {strays > 0 && (
          <span
            className="font-mono tabular-nums"
            style={{ fontSize: 12, color: "var(--color-err)" }}
          >
            ✕ {strays} à côté
          </span>
        )}

        <TempoReading bpm={bpm} />

        <button
          type="button"
          onClick={onCancel}
          className="flex items-center transition-colors hover:bg-white/[0.10] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
          style={{
            gap: 8,
            padding: "7px 12px",
            borderRadius: 8,
            border: "1px solid var(--color-border-subtle)",
            background: "rgba(255, 255, 255, 0.06)",
            color: "var(--color-text-primary)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          <span>Je m’arrête</span>
          <KeyBadge>Esc</KeyBadge>
        </button>
      </div>
    </div>
  );
}

/**
 * The beats of the count-in, filling.
 *
 * Exported because every step that starts a pass has to show the same thing:
 * the chaining step drew a bare `…` instead, which says a wait is happening
 * and not how much of it is left — on the one screen where the player has to
 * come in on the downbeat.
 */
export function CountInDots({ beat, total }: { beat: number; total: number }) {
  return (
    <span className="flex items-center" style={{ gap: 6 }}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background:
              i <= beat
                ? "var(--color-text-primary)"
                : "rgba(255, 255, 255, 0.16)",
          }}
        />
      ))}
      <span className="sr-only">Décompte</span>
    </span>
  );
}

/**
 * The end of a pass where only the rhythm was asked for.
 *
 * Not the ordinary completed card, and the difference is the point: beating
 * the rhythm out is a rehearsal, so it counts no repetition, sets no tempo the
 * unit is remembered by, and offers no way on to the next step. There is one
 * thing to do next, and it is to put the notes back.
 *
 * Same outline as `CompletedFeedback` all the same — rule, headline at 21 px,
 * one act at `ACT_H_PX` — because it is the same moment of the parcours seen
 * from a step that has one door instead of two. What it must not do is look
 * like a *different kind* of screen.
 */
function TappedThrough({
  note,
  onAddNotes,
}: {
  note: string | null;
  onAddNotes: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  // On the act, not on the box. Focus used to land on the card itself, which
  // drew a ring around the whole thing for anyone who had just tapped the
  // rhythm **on the computer keyboard** — the browser holds the last input as
  // a key, so a programmatic focus matches `focus-visible`. Same move, and
  // same reason, as `CompletedFeedback`.
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <div
      style={{
        marginTop: 16,
        paddingTop: 16,
        borderTop: "1px solid var(--color-border-subtle)",
        color: "var(--color-text-primary)",
      }}
    >
      <div
        style={{
          fontSize: 21,
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: "-0.01em",
          textWrap: "balance",
        }}
      >
        C’est ça, le rythme est là.
      </div>

      {note !== null && (
        <div
          style={{
            marginTop: 7,
            fontSize: 14,
            lineHeight: 1.5,
            maxWidth: "46ch",
            color: "var(--color-text-secondary)",
            textWrap: "pretty",
          }}
        >
          {note}
        </div>
      )}

      <button
        ref={ref}
        type="button"
        onClick={onAddNotes}
        className="inline-flex items-center justify-center transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
        style={{
          minHeight: ACT_H_PX,
          marginTop: 20,
          padding: "0 26px",
          borderRadius: 10,
          border: "none",
          background: "#ffffff",
          color: "#0d0d12",
          fontSize: 15,
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Ajouter les notes
      </button>
    </div>
  );
}

// ── Standing facts and offers ─────────────────────────────────────

/**
 * The tempo, in the header, as a fact.
 *
 * In beats per minute and never as a percentage: « 60 % du tempo cible » is a
 * ratio of a number the player did not choose, and it says they are missing
 * 40% of something. The header is also the only horizontal room this panel
 * has going spare, so the fact costs no row of the body.
 */
function TempoReading({ bpm }: { bpm: number }) {
  return (
    <span
      className="font-mono tabular-nums shrink-0"
      style={{ fontSize: 11, color: "var(--color-text-secondary)" }}
    >
      {bpm} BPM
    </span>
  );
}

/**
 * At most one offer per row, and the tempo is not one of them any more.
 *
 * It used to be: a « ▶ Jouer plus vite » link the player had to notice and
 * press, on a ladder the screen never said they were on. The climb happens on
 * its own now (`earnsFasterTempo`), and what is left here is the move the
 * player has to be able to make themselves — **slowing down**, after a pass
 * that did not come out. Asymmetric because the two situations are: going
 * faster is something a pass earns, going slower is something a player
 * decides.
 */
function Offers({
  rate,
  bpm,
  wentThrough,
  clean,
  stopped,
  partial,
  canAccompany,
  spentAccompaniment,
  tapping,
  canTap,
  onStepTempo,
  onWholeUnit,
  onAccompany,
  onTap,
  onAddNotes,
}: {
  rate: number;
  /** The written tempo, to name the one the offer would play at. */
  bpm: number;
  wentThrough: boolean;
  /**
   * Whether the passage came out under the right keys — see `playedClean`.
   *
   * Separate from `wentThrough` because the two claims are different, and the
   * tempo is gated on this one. Crossing without stopping is what the step
   * teaches and stays an achievement with a key caught on the way. Playing it
   * *faster* is a claim a passage full of wrong notes has not earned, and
   * granting the rung anyway is how a player arrives at the written tempo
   * having never played the passage right.
   */
  clean: boolean;
  stopped: boolean;
  /** The pass covers part of the unit: the player scrolled to a bar. */
  partial: boolean;
  canAccompany: boolean;
  spentAccompaniment: ReadonlySet<PracticeHand>;
  tapping: boolean;
  /**
   * Whether beating the rhythm out would teach anything here: the unit has to
   * carry at least two note values — 36.8% carry one, and on those the pulse
   * *is* the rhythm — and the player has to have stopped at least once. An
   * offer on constat, never a rite.
   */
  canTap: boolean;
  onStepTempo: (direction: -1 | 1) => void;
  onWholeUnit: () => void;
  onAccompany: (hand: PracticeHand) => void;
  onTap: () => void;
  onAddNotes: () => void;
}) {
  // Went to the end, but not under the right keys. The tempo stays where it
  // is and the screen says why — and nothing else is offered in its place:
  // proposing to *slow down* after a traversal would read as a demotion for a
  // pass that did the one thing this step asks.
  const held = wentThrough && !clean;
  const slower = !wentThrough && rate > RATE_LADDER[0];

  const accompany =
    stopped &&
    canAccompany &&
    (["L", "R"] as const).filter((h) => !spentAccompaniment.has(h));

  if (tapping) {
    return (
      <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
        <SecondaryLink label="Revenir aux notes" onClick={onAddNotes} />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
      {held && <TempoHeld />}

      {slower && (
        <SecondaryLink
          // Named by what it does and by where it lands. « Un peu plus
          // lentement » described a setting, and the tempo it meant was a
          // number the player had to go and read in the header.
          label={`▶ Jouer plus lentement · ${bpmAt(
            bpm,
            stepRate(rate, -1),
          )} BPM`}
          onClick={() => onStepTempo(-1)}
        />
      )}

      {accompany !== false && accompany.length > 0 && (
        <div className="flex items-center" style={{ gap: 12 }}>
          <span
            style={{ fontSize: 11, color: "var(--color-text-secondary)" }}
          >
            Je joue une main pendant que tu joues l&apos;autre :
          </span>

          {accompany.map((hand) => (
            <SecondaryLink
              key={hand}
              label={hand === "L" ? "la gauche" : "la droite"}
              onClick={() => onAccompany(hand)}
            />
          ))}
        </div>
      )}

      {canTap && (
        <SecondaryLink
          label="Frapper juste le rythme, sans les notes"
          onClick={onTap}
        />
      )}

      {/*
        The way out of a resumed pass. Scrolling back to the first bar does
        the same thing and is the gesture that got here, but a state the
        player entered by accident has to have a named way out of it: the
        panel is otherwise the only place saying the pass covers three bars
        of four.
      */}
      {partial && (
        <SecondaryLink
          label="Reprendre la phrase entière"
          onClick={onWholeUnit}
        />
      )}
    </div>
  );
}

/**
 * Why the rung is not on offer.
 *
 * A sentence and not a disabled button: a control that cannot be pressed
 * invites pressing it, and the reason is the whole message.
 */
function TempoHeld() {
  return (
    <span
      style={{
        justifySelf: "start",
        padding: "2px 0",
        fontSize: 11,
        color: "var(--color-text-secondary)",
      }}
    >
      Le tempo monte quand le passage sort juste.
    </span>
  );
}

function SecondaryLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={OFFER_CLASS}
      style={{ ...OFFER_STYLE, justifySelf: "start" }}
    >
      {label}
    </button>
  );
}

function PrimaryAction({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 8,
        border: "none",
        background: "#ffffff",
        color: "var(--color-bg-deep, #0e0e14)",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      <span>▶ {label}</span>
      <KeyBadge tone="dark">↵</KeyBadge>
    </button>
  );
}

function KeyBadge({
  children,
  tone = "light",
}: {
  children: React.ReactNode;
  tone?: "light" | "dark";
}) {
  return (
    <span
      className="font-mono"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 14,
        padding: "1px 5px",
        borderRadius: 4,
        background:
          tone === "dark" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.08)",
        color:
          tone === "dark"
            ? "var(--color-bg-deep, #0e0e14)"
            : "var(--color-text-secondary)",
        fontSize: 9,
        fontWeight: 600,
        lineHeight: 1.2,
      }}
    >
      {children}
    </span>
  );
}

// ── Copy ──────────────────────────────────────────────────────────

/**
 * The kicker of a run in time: what it is, then which hands it asks for.
 *
 * The first half is dropped when the régime switch is drawn, which says it in
 * a control rather than in a caption. What is left names the hands, which no
 * control on this screen says.
 */
function composeKicker(first: string | null, asked: HandsAsked): string {
  if (first === null) {
    return modeKicker(asked);
  }

  return `${first} · ${modeKicker(asked)}`;
}

function modeKicker(asked: HandsAsked): string {
  if (asked === "lh-only") {
    return "MAIN GAUCHE";
  }

  if (asked === "rh-only") {
    return "MAIN DROITE";
  }

  return "MAINS ENSEMBLE";
}

/**
 * What the screen says before a pass.
 *
 * Three things it can be saying, and only one at a time:
 *
 * - **on arrival**, where the tempo comes from. « À ton tempo » only when it
 *   *is* their tempo — when the practice run measured it a minute ago. A unit
 *   reopened from the stepper, or taken up again in phase 2, opens on what the
 *   unit is remembered by or on its density, and the line says so instead.
 *   Claiming a number is theirs when the app chose it is the one thing this
 *   sentence must not do. Said once either way: a ratio is informative the
 *   first time and noise every time after;
 * - **on a resumed pass**, which bar it starts at, because the roll is the
 *   only other thing saying so and the player is about to hear a count-in;
 * - **the rest of the time**, the instruction, which is the whole contract of
 *   the step: a wrong note is not a reason to stop.
 */
function leadLine(
  asked: HandsAsked,
  writtenBpm: number,
  playedBpm: number,
  passes: number,
  resumeBar: number,
  measured: boolean,
): string {
  if (resumeBar > 1) {
    return `Tu reprends à la mesure ${resumeBar}, jusqu’au bout du passage.`;
  }

  if (passes === 0 && playedBpm !== writtenBpm) {
    return measured
      ? `À ton tempo : ${playedBpm} BPM. Le morceau est écrit à ${writtenBpm}.`
      : `Le morceau est écrit à ${writtenBpm} BPM. On commence à ${playedBpm}.`;
  }

  if (asked === "lh-only") {
    return "Ta main gauche, dans le temps. Va au bout sans t’arrêter.";
  }

  if (asked === "rh-only") {
    return "Ta main droite, dans le temps. Va au bout sans t’arrêter.";
  }

  return "Les deux mains, dans le temps. Si tu rates une note, tu continues.";
}

// ── Helpers ───────────────────────────────────────────────────────

/** Rails the map draws: one per hand the unit actually asks for. */
function mapHands(asked: HandsAsked): readonly PracticeHand[] {
  if (asked === "lh-only") {
    return ["L"];
  }

  if (asked === "rh-only") {
    return ["R"];
  }

  return ["R", "L"];
}
